export type ChartElement = {
  amount: number
  create_timestamp: string
  customerIdentifier: string
}

export type ChartItem = {
  label: string
  amount: number
}

type Group = Record<
  string,
  Record<string, Record<string, Record<string, Record<string, ChartElement[]>>>>
>

// Group chart items
export function groupChartItems(data: ChartElement[]) {
  return data.reduce((acc, item) => {
    const date = new Date(item.create_timestamp)
    const year = date.getFullYear()
    const month = date.getMonth() + 1
    const week = getWeekNumberInMonth(date)
    const day = date.getDate()
    const hour = date.getHours()

    if (!acc[year]) {
      acc[year] = {}
    }

    if (!acc[year][month]) {
      acc[year][month] = {}
    }

    if (!acc[year][month][week]) {
      acc[year][month][week] = {}
    }

    if (!acc[year][month][week][day]) {
      acc[year][month][week][day] = {}
    }

    if (!acc[year][month][week][day][hour]) {
      acc[year][month][week][day][hour] = []
    }

    acc[year][month][week][day][hour].push({
      ...item,
      create_timestamp: new Date(item.create_timestamp).toString(),
    })

    return acc
  }, {} as Group)
}

// Get chart items fo year
export function getYearChartItems(groups: Group, year: number) {
  const currentYearItems = groups[year]
  let maxAmount = 0
  const items = Array.from({ length: 12 }, (_, i) => {
    const _index = i + 1
    const _date = new Date(year, i)

    if (!currentYearItems[_index]) {
      return {
        label: _date.toString(),
        abount: 0,
      }
    }

    const amount = Object.entries(currentYearItems[_index]).reduce(
      (monthAcc, [, weekData]) => {
        monthAcc += Object.entries(weekData).reduce((weekAcc, [, dayData]) => {
          weekAcc += Object.entries(dayData).reduce((dayAcc, [, hourAcc]) => {
            dayAcc += hourAcc.reduce((acc, item) => (acc += item.amount), 0)
            return dayAcc
          }, 0)
          return weekAcc
        }, 0)
        return monthAcc
      },
      0,
    )
    if (amount > maxAmount) maxAmount = amount
    return {
      label: _date.toString(),
      amount,
    }
  }) as Array<{
    label: string
    amount: number
  }>

  return { items, maxAmount }
}

// Get chart items for month
export function getMonthChartItems(groups: Group, year: number, month: number) {
  const currentYearItems = groups[year]
  const currentMonthItems = currentYearItems[month]

  let maxAmount = 0

  const { days } = getDaysInMonth(year, month)
  const items = Array.from({ length: days }, (_, i) => {
    const index = i + 1
    const _date = new Date(year, month - 1, index)
    const currentWeek = getWeekNumberInMonth(new Date(year, month - 1, index))

    if (!currentMonthItems?.[currentWeek]?.[index]) {
      return {
        label: _date.toString(),
        amount: 0,
      }
    }

    const amount = Object.entries(currentMonthItems[currentWeek][index]).reduce(
      (dayAcc, [, hourData]) => {
        return (dayAcc += hourData.reduce((dayAcc, hourData) => {
          return (dayAcc += hourData.amount)
        }, 0))
      },
      0,
    )
    if (amount > maxAmount) maxAmount = amount
    return {
      label: _date.toString(),
      amount,
    }
  }) as Array<{
    label: string
    amount: number
  }>
  return { items, maxAmount }
}

// Get chart items for week
export function getWeekChartItems(
  groups: Group,
  year: number,
  month: number,
  week: number,
) {
  let maxAmount = 0
  const items: Array<{
    label: string
    amount: number
  }> = []
  const days = getDaysOfWeekFromDate(new Date(year, month - 1), week)
  days.forEach((date) => {
    const _year = date.getFullYear()
    const _month = date.getMonth() + 1
    const _day = date.getDate()
    const currentYearItems = groups?.[_year]
    const currentMonthItems = currentYearItems?.[_month]
    const currentWeek = getWeekNumberInMonth(new Date(_year, _month - 1, _day))
    const hours = currentMonthItems?.[currentWeek]?.[_day]

    Array.from({ length: 24 }).forEach((_, i) => {
      const _date = new Date(_year, _month - 1, _day, i)
      const hour = hours?.[i]
      if (!hour) {
        items.push({
          label: _date.toString(),
          amount: 0,
        })
      } else {
        const amount = hour.reduce((acc, el) => (acc += el.amount), 0)
        if (amount > maxAmount) maxAmount = amount
        items.push({
          label: _date.toString(),
          amount: amount,
        })
      }
    })
  })

  return { items, maxAmount }
}

// Get Week Number By Date
function getWeekNumberInMonth(date: Date) {
  const month = date.getMonth()
  const year = date.getFullYear()
  const firstDayOfMonth = new Date(year, month, 1)
  const firstDayOfWeek = firstDayOfMonth.getDay()
  const firstMondayOfMonth =
    firstDayOfMonth.getDate() + ((8 - firstDayOfWeek) % 7)
  const dayOfMonth = date.getDate()
  const weekNumber = Math.ceil((dayOfMonth - firstMondayOfMonth + 1) / 7)

  return (weekNumber + 1).toString()
}

// Get days count for month and year
// If you pass 2023 as year and month (Jan) - 1 it will return December 2022
function getDaysInMonth(year: number, month: number) {
  const date = new Date(year, month, 0)
  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    days: date.getDate(),
  }
}

// Get days in week
// For example I want to get first week in first week.
// It'll return [27 Feb, 28 Feb, 1 Mar, 2 Mar, 3 Mar, 4 Mar, 5 Mar]
function getDaysOfWeekFromDate(date: Date, startWeek = 1) {
  const daysOfWeek = []
  const dayOfWeek = date.getDay() // get day of week (0-6)

  // calculate date of first day of week (Monday)
  const firstDayOfWeek = new Date(date)
  const diff =
    date.getDate() -
    dayOfWeek +
    (dayOfWeek === 0 ? -6 : 1) +
    (startWeek - 1) * 7
  firstDayOfWeek.setDate(diff)

  // loop through remaining days of week
  for (let i = 0; i < 7; i++) {
    const day = new Date(firstDayOfWeek)
    day.setDate(firstDayOfWeek.getDate() + i)
    daysOfWeek.push(day)
  }

  return daysOfWeek
}

export function getWeekChartLabel(date: Date) {
  const _date = new Date(date)
  const _day = _date.getDate()
  const _hour = _date.getHours()
  return _hour.toString() === '0'
    ? `${months[_date.getMonth()]} ${_day} ${_date.getFullYear()}`
    : ''
}

export function getWeekChartTooktip(date: Date) {
  const _date = new Date(date)
  const _dayCount = _date.getDay()
  const d = days[_dayCount]
  return `${_date.getDate()} ${
    months[_date.getMonth()]
  } ${_date.getFullYear()} (${d}), ${_date.getHours()}:${_date.getMinutes()}0`
}

export function getMonthChartLabel(date: Date) {
  const _date = new Date(date)
  return `${months[_date.getMonth()]} ${_date.getDate()}`
}

export function getMonthChartTooltip(date: Date) {
  const _date = new Date(date)
  const _dayCount = _date.getDay()
  const d = days[_dayCount]
  return `${months[_date.getMonth()]} ${_date.getDate()} (${d})`
}

export function getYearChartLabel(date: Date) {
  const _date = new Date(date)
  return `${months[_date.getMonth()]} ${_date.getFullYear()}`
}

export function getMonthNameByIndex(month: number) {
  return months[month]
}

export function getWeekName(year: number, month: number, week: number) {
  const days = getDaysOfWeekFromDate(new Date(year, month - 1), week)
  const firstDay = days[0]
  const lastDay = days[days.length - 1]
  return `Week ${week} (${firstDay.getDate()} ${
    months[firstDay.getMonth()]
  } - ${lastDay.getDate()} ${months[lastDay.getMonth()]})`
}

const months = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

const days = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
]
